import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { PopupContainer } from 'shared/components/popups';
import {
  PopupEventType,
  PopupPosition
} from 'shared/entities/components/Popup';
import WithMeta from 'shared/newComponents/form/WithMeta';
import {
  SelectorBaseProps,
  SelectorSize,
  SelectorTriggerSize
} from 'shared/newEntities/components/Selector';

import Content from './Content';
import Trigger from './Trigger';

import './SelectorBase.modules.scss';

const getActualTriggerSize = ({
  size,
  triggerSize
}: {
  size?: SelectorSize;
  triggerSize?: SelectorTriggerSize;
}) => {
  if (triggerSize) {
    return triggerSize;
  }

  if (size === SelectorSize.small) {
    return SelectorTriggerSize.sm;
  }

  if (size === SelectorSize.medium) {
    return SelectorTriggerSize.m;
  }

  return SelectorTriggerSize.xl;
};

const SELECTOR_POPUP_STABLE_WIDTH = 320;

const SelectorBase: React.FC<
  SelectorBaseProps & {
    optionsLength: number;
    stablePopupSize?: boolean;
  }
> = (props) => {
  const {
    position = PopupPosition.bottomLeft,
    on = PopupEventType.click,
    closeOnDocumentClick,
    withoutBorder,
    sameWidth = true,
    onClose,
    onOpen,
    disabled,
    trigger,
    renderTrigger,
    children,
    contentTopNode,
    triggerSize,
    size,
    optionsLength,
    stablePopupSize = false,
    triggerClassName,
    isOpen,
    ...metaProps
  } = props;
  const popupRef = React.useRef<{
    container: HTMLDivElement;
    setPosition: () => void;
  } | null>(null);
  const [width, setWidth] = React.useState<number | string>('initial');

  React.useEffect(() => {
    let observer: ResizeObserver | null = null;

    if (popupRef.current?.container && sameWidth) {
      setWidth(popupRef.current.container.clientWidth);

      observer = new ResizeObserver(() => {
        if (popupRef.current?.container) {
          setWidth(popupRef.current.container.clientWidth);
        }
      });

      observer.observe(popupRef.current.container);
    }

    return () => {
      if (observer && sameWidth) {
        observer.disconnect();
      }
    };
  }, []);

  React.useEffect(() => {
    popupRef.current?.setPosition();
  }, [optionsLength]);

  const actualTriggerSize = getActualTriggerSize({ size, triggerSize });
  const contentWidth =
    stablePopupSize && !sameWidth ? SELECTOR_POPUP_STABLE_WIDTH : width;

  return (
    <WithMeta {...metaProps}>
      <PopupContainer
        ref={popupRef}
        position={position}
        onClose={onClose}
        onOpen={onOpen}
        isOpen={isOpen}
        on={on}
        gap={6}
        disabled={disabled}
        styleName="selector-container"
        closeOnDocumentClick={closeOnDocumentClick}
        trigger={(isOpen) => {
          if (renderTrigger) {
            return <div>{renderTrigger({ isOpen })}</div>;
          }

          return (
            <div>
              <Trigger
                withoutBorder={withoutBorder}
                isSelectorOpened={isOpen}
                isError={!!metaProps.error}
                disabled={disabled}
                size={actualTriggerSize}
                triggerClassName={triggerClassName}
              >
                {trigger!}
              </Trigger>
            </div>
          );
        }}
      >
        {(onClose, isOpen) => {
          return (
            <Content
              width={contentWidth}
              onClose={onClose}
              isOpen={isOpen}
              extraTopNode={contentTopNode}
            >
              {children}
            </Content>
          );
        }}
      </PopupContainer>
    </WithMeta>
  );
};

export default SelectorBase;
